import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Wallet, Download, Copy, Webhook, RefreshCw, CheckCircle2, AlertCircle, RotateCcw, ChevronRight, Plus, Pencil } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const fmtEur = (v) => (typeof v === "number" ? v : parseFloat(v || 0))
  .toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE");
  } catch (e) { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    paid: { label: "Bezahlt", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
    open: { label: "Offen", cls: "bg-amber-100 text-amber-700 border-amber-300" },
    partially_paid: { label: "Teilzahlung", cls: "bg-blue-100 text-blue-700 border-blue-300" },
    refunded: { label: "Storniert", cls: "bg-rose-100 text-rose-700 border-rose-300" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-gray-100 text-gray-700 border-gray-300" };
  return <span className={`text-xs px-2 py-0.5 rounded border ${s.cls}`}>{s.label}</span>;
};

const TabButton = ({ active, onClick, children, testid, badge }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
      active ? "border-[#0d3b66] text-[#0d3b66]" : "border-transparent text-gray-600 hover:text-[#0d3b66]"
    }`}
  >
    {children}
    {badge != null && (
      <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${active ? "bg-[#0d3b66] text-white" : "bg-gray-200 text-gray-700"}`}>
        {badge}
      </span>
    )}
  </button>
);

const Buchhaltung = () => {
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], count: 0, total_amount: 0, net_amount: 0, refund_amount: 0 });
  const [openData, setOpenData] = useState({ items: [], count: 0, total_offen: 0 });
  const [ratenData, setRatenData] = useState({ items: [], count: 0, total_saldo: 0 });
  const [refundData, setRefundData] = useState({ items: [], count: 0, total_refunded: 0 });

  // Filter
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Webhook dialog
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);

  // Manual booking dialog
  const [manualOpen, setManualOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    external_id: "", occurred_at: new Date().toISOString().slice(0, 10),
    course_id: "", course_title: "", buyer_type: "selbstzahler",
    amount_total: "", payment_method: "manual", status: "paid",
    raten: "", raten_paid: "", monthly_amount: "",
    first_name: "", last_name: "", email: "", phone: "", street: "", zip_code: "", city: "", organisation: "",
    note: "",
  };
  const [form, setForm] = useState(emptyForm);
  const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Export
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [exportYear, setExportYear] = useState(currentYear);
  const [exportMonth, setExportMonth] = useState(currentMonth);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFrom) params.set("from_date", filterFrom);
      if (filterTo) params.set("to_date", filterTo);
      if (filterMethod) params.set("payment_method", filterMethod);
      if (filterStatus) params.set("status", filterStatus);
      const [all, open, raten, refunds] = await Promise.all([
        axios.get(`${API}/payments?${params.toString()}`),
        axios.get(`${API}/payments/open-items`),
        axios.get(`${API}/payments/installments`),
        axios.get(`${API}/payments/refunds`),
      ]);
      setData(all.data);
      setOpenData(open.data);
      setRatenData(raten.data);
      setRefundData(refunds.data);
    } catch (e) {
      toast.error("Fehler beim Laden der Zahlungen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo, filterMethod, filterStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadWebhookInfo = async () => {
    try {
      const res = await axios.get(`${API}/payments/webhook-info`);
      // Wichtiger Fix: URL immer aus REACT_APP_BACKEND_URL bilden (öffentliche URL)
      const publicUrl = BACKEND_URL ? `${BACKEND_URL}/api/ingest/dickies-helden` : res.data.webhook_url;
      setWebhookInfo({ ...res.data, webhook_url: publicUrl });
      setWebhookOpen(true);
    } catch (e) {
      toast.error("Webhook-Info nicht abrufbar", { description: e?.response?.data?.detail || e.message });
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} kopiert`),
      () => toast.error("Kopieren fehlgeschlagen"),
    );
  };

  const markPaid = async (payment) => {
    const note = window.prompt(`Rechnung ${payment.external_id} als bezahlt markieren?\n\nOptional: Notiz (z.B. Überweisungsreferenz)`, "");
    if (note === null) return;
    try {
      await axios.post(`${API}/payments/${payment.id}/mark-paid`, { note });
      toast.success("Als bezahlt markiert");
      loadAll();
    } catch (e) {
      toast.error("Fehler", { description: e?.response?.data?.detail || e.message });
    }
  };

  const openNewManual = () => {
    setEditingId(null);
    setForm(emptyForm);
    setManualOpen(true);
  };

  const openEdit = (p) => {
    const c = p.customer || {};
    setEditingId(p.id);
    setForm({
      external_id: p.external_id || "",
      occurred_at: (p.occurred_at || "").slice(0, 10),
      course_id: p.course_id || "",
      course_title: p.course_title || "",
      buyer_type: p.buyer_type || "selbstzahler",
      amount_total: p.amount_total ?? "",
      payment_method: p.payment_method || "manual",
      status: p.status || "paid",
      raten: p.raten ?? "",
      raten_paid: p.raten_paid ?? "",
      monthly_amount: p.monthly_amount ?? "",
      first_name: c.first_name || "", last_name: c.last_name || "",
      email: c.email || "", phone: c.phone || "",
      street: c.street || "", zip_code: c.zip_code || "", city: c.city || "",
      organisation: c.organisation || "",
      note: p.manual_note || "",
    });
    setManualOpen(true);
  };

  const saveManual = async () => {
    if (!form.amount_total || parseFloat(form.amount_total) <= 0) {
      toast.error("Betrag muss > 0 sein");
      return;
    }
    if (!form.course_title && !form.course_id) {
      toast.error("Kurs-Titel oder Kurs-ID erforderlich");
      return;
    }
    const payload = {
      external_id: form.external_id || null,
      occurred_at: form.occurred_at ? `${form.occurred_at}T00:00:00Z` : null,
      kind: "kurs",
      course_id: form.course_id || null,
      course_title: form.course_title || null,
      customer: {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone,
        street: form.street, zip_code: form.zip_code, city: form.city,
        organisation: form.organisation,
      },
      buyer_type: form.buyer_type,
      amount_total: parseFloat(form.amount_total),
      currency: "EUR",
      payment_method: form.payment_method,
      status: form.status,
      raten: form.raten ? parseInt(form.raten) : null,
      raten_paid: form.raten_paid ? parseInt(form.raten_paid) : null,
      monthly_amount: form.monthly_amount ? parseFloat(form.monthly_amount) : null,
      note: form.note || null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/payments/${editingId}`, payload);
        toast.success("Buchung aktualisiert");
      } else {
        const res = await axios.post(`${API}/payments/manual`, payload);
        toast.success("Buchung angelegt", { description: `Beleg: ${res.data.external_id}` });
      }
      setManualOpen(false);
      loadAll();
    } catch (e) {
      toast.error("Fehler", { description: e?.response?.data?.detail || e.message });
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Buchung ${p.external_id} endgültig löschen?\n\nBesser: als Storno markieren (via Kursplattform) statt löschen.`)) return;
    try {
      await axios.delete(`${API}/payments/${p.id}`);
      toast.success("Buchung gelöscht");
      loadAll();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    }
  };

  const downloadDatev = async () => {
    try {
      const res = await axios.get(`${API}/payments/export/datev-csv?year=${exportYear}&month=${exportMonth}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `DATEV_${exportYear}-${String(exportMonth).padStart(2, "0")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("DATEV-CSV heruntergeladen");
    } catch (e) {
      toast.error("Export fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="buchhaltung-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#0d3b66] flex items-center gap-3">
            <Wallet size={30} /> Buchhaltung
          </h1>
          <p className="text-sm text-gray-600 mt-1">Zahlungen aus dickies-helden.de · Offene Posten · Raten · Stornos · DATEV-Export</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openNewManual} data-testid="btn-new-manual-payment">
            <Plus size={16} className="mr-2" /> Neue Buchung
          </Button>
          <Button variant="outline" onClick={loadWebhookInfo} data-testid="btn-webhook-info">
            <Webhook size={16} className="mr-2" /> Webhook-Info
          </Button>
          <Button variant="outline" onClick={loadAll} data-testid="btn-refresh-buchhaltung">
            <RefreshCw size={16} className={loading ? "mr-2 animate-spin" : "mr-2"} /> Aktualisieren
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card data-testid="kpi-total-amount">
          <CardContent className="pt-6">
            <div className="text-xs text-gray-600 uppercase">Umsatz</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{fmtEur(data.total_amount)}</div>
            <div className="text-xs text-gray-500 mt-1">{data.count} Buchungen</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-net-amount">
          <CardContent className="pt-6">
            <div className="text-xs text-gray-600 uppercase">Netto (nach Storno)</div>
            <div className="text-2xl font-bold text-[#0d3b66] mt-1">{fmtEur(data.net_amount)}</div>
            <div className="text-xs text-gray-500 mt-1">Storno: {fmtEur(data.refund_amount)}</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-open-items">
          <CardContent className="pt-6">
            <div className="text-xs text-gray-600 uppercase">Offene Posten</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{fmtEur(openData.total_offen)}</div>
            <div className="text-xs text-gray-500 mt-1">{openData.count} Rechnungen</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-raten-saldo">
          <CardContent className="pt-6">
            <div className="text-xs text-gray-600 uppercase">Raten-Saldo offen</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{fmtEur(ratenData.total_saldo)}</div>
            <div className="text-xs text-gray-500 mt-1">{ratenData.count} Ratenzahlungen</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card className="mb-4">
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div>
              <Label className="text-xs">Von</Label>
              <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="filter-from-date" />
            </div>
            <div>
              <Label className="text-xs">Bis</Label>
              <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="filter-to-date" />
            </div>
            <div>
              <Label className="text-xs">Zahlart</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} data-testid="filter-method">
                <option value="">Alle</option>
                <option value="online">Online (Karte/SEPA)</option>
                <option value="rechnung">Rechnung</option>
                <option value="raten">Raten</option>
                <option value="klarna">Klarna</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
                <option value="">Alle</option>
                <option value="paid">Bezahlt</option>
                <option value="open">Offen</option>
                <option value="partially_paid">Teilzahlung</option>
                <option value="refunded">Storniert</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">DATEV-Export</Label>
                <div className="flex gap-1">
                  <Input type="number" min="2020" max="2100" value={exportYear} onChange={(e) => setExportYear(parseInt(e.target.value))} className="w-24" data-testid="export-year" />
                  <select className="h-10 border rounded px-2 text-sm" value={exportMonth} onChange={(e) => setExportMonth(parseInt(e.target.value))} data-testid="export-month">
                    {["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"].map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Button className="bg-[#0d3b66] hover:bg-[#0a2f52]" onClick={downloadDatev} data-testid="btn-datev-export">
                <Download size={16} className="mr-2" /> CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 flex gap-1">
        <TabButton active={tab === "all"} onClick={() => setTab("all")} testid="tab-all" badge={data.count}>Alle Zahlungen</TabButton>
        <TabButton active={tab === "open"} onClick={() => setTab("open")} testid="tab-open" badge={openData.count}>Offene Posten</TabButton>
        <TabButton active={tab === "raten"} onClick={() => setTab("raten")} testid="tab-raten" badge={ratenData.count}>Ratenzahlungen</TabButton>
        <TabButton active={tab === "refunds"} onClick={() => setTab("refunds")} testid="tab-refunds" badge={refundData.count}>Storno / Erstattung</TabButton>
      </div>

      {/* Content */}
      {tab === "all" && <PaymentsTable items={data.items} onEdit={openEdit} onDelete={deletePayment} />}
      {tab === "open" && <OpenItemsTable items={openData.items} onMarkPaid={markPaid} onEdit={openEdit} />}
      {tab === "raten" && <InstallmentsTable items={ratenData.items} onEdit={openEdit} />}
      {tab === "refunds" && <RefundsTable items={refundData.items} />}

      {/* Manual Booking Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <><Pencil size={20} /> Buchung bearbeiten</> : <><Plus size={20} /> Neue Buchung anlegen</>}
            </DialogTitle>
            <DialogDescription>
              {editingId ? "Werte ändern und speichern." : "Für Buchungen die NICHT über dickies-helden.de kommen (z.B. Barzahlung, Direktüberweisung, telefonisch)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Datum *</Label>
                <Input type="date" value={form.occurred_at} onChange={(e) => setF("occurred_at", e.target.value)} data-testid="form-occurred-at" />
              </div>
              <div>
                <Label className="text-xs">Belegnummer (optional)</Label>
                <Input placeholder="wird automatisch generiert" value={form.external_id} onChange={(e) => setF("external_id", e.target.value)} data-testid="form-external-id" disabled={!!editingId} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kurs-ID</Label>
                <Input placeholder="z.B. pflegeassistent_lg1_lg2" value={form.course_id} onChange={(e) => setF("course_id", e.target.value)} data-testid="form-course-id" />
              </div>
              <div>
                <Label className="text-xs">Kurs-Titel *</Label>
                <Input placeholder="z.B. Pflegeassistent LG1/LG2" value={form.course_title} onChange={(e) => setF("course_title", e.target.value)} data-testid="form-course-title" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Betrag (EUR) *</Label>
                <Input type="number" step="0.01" placeholder="1990.00" value={form.amount_total} onChange={(e) => setF("amount_total", e.target.value)} data-testid="form-amount" />
              </div>
              <div>
                <Label className="text-xs">Zahlart</Label>
                <select className="w-full h-10 border rounded px-3 text-sm" value={form.payment_method} onChange={(e) => setF("payment_method", e.target.value)} data-testid="form-method">
                  <option value="manual">Manuell / Bar</option>
                  <option value="online">Online (Karte/SEPA)</option>
                  <option value="rechnung">Rechnung</option>
                  <option value="raten">Raten</option>
                  <option value="klarna">Klarna</option>
                  <option value="ueberweisung">Überweisung</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <select className="w-full h-10 border rounded px-3 text-sm" value={form.status} onChange={(e) => setF("status", e.target.value)} data-testid="form-status">
                  <option value="paid">Bezahlt</option>
                  <option value="open">Offen (Rechnung)</option>
                  <option value="partially_paid">Teilzahlung</option>
                  <option value="refunded">Storniert</option>
                </select>
              </div>
            </div>

            {form.payment_method === "raten" && (
              <div className="grid grid-cols-3 gap-3 border-t pt-3">
                <div>
                  <Label className="text-xs">Anzahl Raten</Label>
                  <Input type="number" placeholder="12" value={form.raten} onChange={(e) => setF("raten", e.target.value)} data-testid="form-raten" />
                </div>
                <div>
                  <Label className="text-xs">Bereits bezahlt</Label>
                  <Input type="number" placeholder="0" value={form.raten_paid} onChange={(e) => setF("raten_paid", e.target.value)} data-testid="form-raten-paid" />
                </div>
                <div>
                  <Label className="text-xs">Monatsbetrag</Label>
                  <Input type="number" step="0.01" placeholder="165.83" value={form.monthly_amount} onChange={(e) => setF("monthly_amount", e.target.value)} data-testid="form-monthly-amount" />
                </div>
              </div>
            )}

            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-2">Kunde</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Vorname</Label>
                  <Input value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} data-testid="form-first-name" />
                </div>
                <div>
                  <Label className="text-xs">Nachname</Label>
                  <Input value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} data-testid="form-last-name" />
                </div>
                <div>
                  <Label className="text-xs">E-Mail</Label>
                  <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="form-email" />
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="form-phone" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Organisation (z.B. Pflegedienst)</Label>
                  <Input value={form.organisation} onChange={(e) => setF("organisation", e.target.value)} data-testid="form-organisation" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Straße</Label>
                  <Input value={form.street} onChange={(e) => setF("street", e.target.value)} data-testid="form-street" />
                </div>
                <div>
                  <Label className="text-xs">PLZ</Label>
                  <Input value={form.zip_code} onChange={(e) => setF("zip_code", e.target.value)} data-testid="form-zip" />
                </div>
                <div>
                  <Label className="text-xs">Ort</Label>
                  <Input value={form.city} onChange={(e) => setF("city", e.target.value)} data-testid="form-city" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Buchertyp</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={form.buyer_type} onChange={(e) => setF("buyer_type", e.target.value)} data-testid="form-buyer-type">
                <option value="selbstzahler">Selbstzahler</option>
                <option value="pflegedienst">Pflegedienst</option>
              </select>
            </div>

            <div>
              <Label className="text-xs">Interne Notiz</Label>
              <Input placeholder="z.B. Bar bezahlt in bar am 15.02., Beleg #123" value={form.note} onChange={(e) => setF("note", e.target.value)} data-testid="form-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} data-testid="btn-cancel-manual">Abbrechen</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveManual} data-testid="btn-save-manual">
              {editingId ? "Änderungen speichern" : "Buchung anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook Info Dialog */}
      <Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Webhook size={20} /> Webhook-Zugang für dickies-helden.de</DialogTitle>
            <DialogDescription>
              Trage diese beiden Werte in deiner Kursplattform ein. Die Plattform sendet dann bei jedem Zahlungsereignis
              automatisch die Daten an dein CRM.
            </DialogDescription>
          </DialogHeader>
          {webhookInfo && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Webhook-URL (Ziel)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={webhookInfo.webhook_url} data-testid="webhook-url" className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url, "URL")} data-testid="btn-copy-url">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">HTTP-Header</Label>
                <Input readOnly value={webhookInfo.secret_header} className="font-mono text-xs mt-1" />
              </div>
              <div>
                <Label className="text-xs">Header-Wert (Secret) — GEHEIM, nicht teilen</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={webhookInfo.secret_value} data-testid="webhook-secret" className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.secret_value, "Secret")} data-testid="btn-copy-secret">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Unterstützte Events (JSON-Feld `event`)</Label>
                <div className="mt-1 text-xs font-mono bg-gray-50 p-3 rounded border">
                  {webhookInfo.supported_events.map((e) => <div key={e}>• {e}</div>)}
                </div>
              </div>
              <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 p-3 rounded">
                <strong>Hinweis:</strong> Sende eine POST-Request mit <code>Content-Type: application/json</code> und dem Header <code>X-Webhook-Secret</code>.
                Die Idempotenz erfolgt über das Feld <code>external_id</code>, d.h. Doppelzustellungen werden automatisch erkannt.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setWebhookOpen(false)} data-testid="btn-close-webhook-info">Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PaymentsTable = ({ items, onEdit, onDelete }) => (
  <div className="overflow-x-auto" data-testid="table-all-container">
    <table className="w-full text-sm">
      <thead className="bg-[#0d3b66] text-white">
        <tr>
          <th className="text-left px-3 py-2">Datum</th>
          <th className="text-left px-3 py-2">Beleg</th>
          <th className="text-left px-3 py-2">Kunde</th>
          <th className="text-left px-3 py-2">Kurs</th>
          <th className="text-left px-3 py-2">Zahlart</th>
          <th className="text-right px-3 py-2">Betrag</th>
          <th className="text-center px-3 py-2">Status</th>
          <th className="text-center px-3 py-2">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={8} className="text-center py-8 text-gray-500">Keine Zahlungen im gewählten Zeitraum.</td></tr>
        )}
        {items.map((p, idx) => {
          const cust = p.customer || {};
          const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || cust.organisation || "—";
          const isManual = p.source === "manual" || (p.external_id || "").startsWith("MAN-");
          return (
            <tr key={p.id || idx} className="border-b hover:bg-gray-50" data-testid={`row-payment-${idx}`}>
              <td className="px-3 py-2">{fmtDate(p.occurred_at)}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {p.external_id}
                {isManual && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-300">MANUELL</span>}
              </td>
              <td className="px-3 py-2">{name}</td>
              <td className="px-3 py-2">{p.course_title || p.course_id}</td>
              <td className="px-3 py-2">{p.payment_method}</td>
              <td className="px-3 py-2 text-right font-semibold">{fmtEur(p.amount_total)}</td>
              <td className="px-3 py-2 text-center"><StatusBadge status={p.status} /></td>
              <td className="px-3 py-2 text-center">
                <div className="flex gap-1 justify-center">
                  <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`btn-edit-${idx}`} title="Bearbeiten">
                    <Pencil size={12} />
                  </Button>
                  <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onDelete(p)} data-testid={`btn-delete-${idx}`} title="Löschen">
                    ×
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const OpenItemsTable = ({ items, onMarkPaid, onEdit }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-amber-600 text-white">
        <tr>
          <th className="text-left px-3 py-2">Rechnungsdatum</th>
          <th className="text-left px-3 py-2">Beleg</th>
          <th className="text-left px-3 py-2">Kunde</th>
          <th className="text-left px-3 py-2">Kurs</th>
          <th className="text-right px-3 py-2">Betrag</th>
          <th className="text-center px-3 py-2">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={6} className="text-center py-8 text-emerald-700">
            <CheckCircle2 size={30} className="inline mr-2" /> Keine offenen Posten. Alle Rechnungen sind bezahlt.
          </td></tr>
        )}
        {items.map((p, idx) => {
          const cust = p.customer || {};
          const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || cust.organisation || "—";
          return (
            <tr key={p.id || idx} className="border-b hover:bg-amber-50" data-testid={`row-open-${idx}`}>
              <td className="px-3 py-2">{fmtDate(p.occurred_at)}</td>
              <td className="px-3 py-2 font-mono text-xs">{p.external_id}</td>
              <td className="px-3 py-2">{name}<br /><span className="text-xs text-gray-500">{cust.email}</span></td>
              <td className="px-3 py-2">{p.course_title || p.course_id}</td>
              <td className="px-3 py-2 text-right font-semibold text-amber-700">{fmtEur(p.amount_total)}</td>
              <td className="px-3 py-2 text-center">
                <div className="flex gap-1 justify-center flex-wrap">
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => onMarkPaid(p)} data-testid={`btn-mark-paid-${idx}`}>
                    <CheckCircle2 size={14} className="mr-1" /> Bezahlt
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`btn-edit-open-${idx}`} title="Bearbeiten">
                    <Pencil size={12} />
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const InstallmentsTable = ({ items, onEdit }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-blue-700 text-white">
        <tr>
          <th className="text-left px-3 py-2">Startdatum</th>
          <th className="text-left px-3 py-2">Kunde</th>
          <th className="text-left px-3 py-2">Kurs</th>
          <th className="text-center px-3 py-2">Raten</th>
          <th className="text-right px-3 py-2">Monatsbetrag</th>
          <th className="text-right px-3 py-2">Saldo offen</th>
          <th className="text-left px-3 py-2">Nächste Fälligkeit</th>
          <th className="text-center px-3 py-2">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={8} className="text-center py-8 text-gray-500">Keine aktiven Ratenzahlungen.</td></tr>
        )}
        {items.map((p, idx) => {
          const cust = p.customer || {};
          const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || cust.organisation || "—";
          const pct = p.raten ? Math.round(((p.raten_paid || 0) / p.raten) * 100) : 0;
          return (
            <tr key={p.id || idx} className="border-b hover:bg-blue-50" data-testid={`row-raten-${idx}`}>
              <td className="px-3 py-2">{fmtDate(p.occurred_at)}</td>
              <td className="px-3 py-2">{name}<br /><span className="text-xs text-gray-500">{cust.email}</span></td>
              <td className="px-3 py-2">{p.course_title || p.course_id}</td>
              <td className="px-3 py-2 text-center">
                <div className="text-xs font-semibold">{p.raten_paid || 0} / {p.raten || 0}</div>
                <div className="h-1.5 bg-gray-200 rounded mt-1 overflow-hidden">
                  <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                </div>
              </td>
              <td className="px-3 py-2 text-right">{fmtEur(p.monthly_amount)}</td>
              <td className="px-3 py-2 text-right font-semibold text-blue-700">{fmtEur(p.saldo_offen)}</td>
              <td className="px-3 py-2 text-xs">{p.next_due ? fmtDate(p.next_due) : "—"}</td>
              <td className="px-3 py-2 text-center">
                <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`btn-edit-raten-${idx}`} title="Bearbeiten">
                  <Pencil size={12} />
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const RefundsTable = ({ items }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-rose-700 text-white">
        <tr>
          <th className="text-left px-3 py-2">Storno-Datum</th>
          <th className="text-left px-3 py-2">Beleg</th>
          <th className="text-left px-3 py-2">Kunde</th>
          <th className="text-left px-3 py-2">Kurs</th>
          <th className="text-right px-3 py-2">Erstattet</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={5} className="text-center py-8 text-gray-500">Keine Stornos.</td></tr>
        )}
        {items.map((p, idx) => {
          const cust = p.customer || {};
          const name = `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || cust.organisation || "—";
          return (
            <tr key={p.id || idx} className="border-b hover:bg-rose-50" data-testid={`row-refund-${idx}`}>
              <td className="px-3 py-2">{fmtDate(p.refunded_at || p.occurred_at)}</td>
              <td className="px-3 py-2 font-mono text-xs">{p.external_id}</td>
              <td className="px-3 py-2">{name}</td>
              <td className="px-3 py-2">{p.course_title || p.course_id}</td>
              <td className="px-3 py-2 text-right font-semibold text-rose-700">-{fmtEur(p.amount_total)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default Buchhaltung;
